import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function EnthalpyChangeCalculator() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Chemistry", url: "https://calclab.app/chemistry" },
      { name: "Enthalpy Change Calculator", url: "https://calclab.app/calculator/enthalpy-change" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Enthalpy Change Calculator",
      "Calculate heat energy change (ΔH) using q = mcΔT formula. Free online thermochemistry calculator.",
      "https://calclab.app/calculator/enthalpy-change",
      lang
    )
  ]);
  const [mass, setMass] = useState("");
  const [specificHeat, setSpecificHeat] = useState("");
  const [tempChange, setTempChange] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const m = parseFloat(mass);
    const c = parseFloat(specificHeat);
    const dt = parseFloat(tempChange);

    if (isNaN(m) || isNaN(c) || isNaN(dt)) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers.",
        variant: "destructive",
      });
      return;
    }

    const q = m * c * dt;
    setResult(q);

    toast({
      title: "Calculation Complete",
      description: `ΔH = ${q.toFixed(2)} J`,
    });
  };

  const reset = () => {
    setMass("");
    setSpecificHeat("");
    setTempChange("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Enthalpy Change Calculator" 
      calculatorPath="/calculator/enthalpy-change"
      category="chemistry"
      subcategory="Thermochemistry"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Enthalpy Change Calculator</CardTitle>
            <CardDescription>
              Calculate heat energy change (ΔH)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mass">Mass (g)</Label>
                <Input
                  id="mass"
                  type="number"
                  step="any"
                  value={mass}
                  onChange={(e) => setMass(e.target.value)}
                  placeholder="Enter mass"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="specificHeat">Specific Heat (J/g·°C)</Label>
                <Input
                  id="specificHeat"
                  type="number"
                  step="any"
                  value={specificHeat}
                  onChange={(e) => setSpecificHeat(e.target.value)}
                  placeholder="Enter specific heat capacity"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tempChange">Temperature Change (°C)</Label>
                <Input
                  id="tempChange"
                  type="number"
                  step="any"
                  value={tempChange}
                  onChange={(e) => setTempChange(e.target.value)}
                  placeholder="ΔT = T_final - T_initial"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Enthalpy Change (ΔH)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-2xl font-bold break-all overflow-wrap-anywhere">{result.toFixed(2)} J</p>
                        <p className="text-sm text-muted-foreground mt-2">
                          {result > 0 ? "Endothermic (heat absorbed)" : "Exothermic (heat released)"}
                        </p>
                      </div>
                      <CopyResultButton value={`${result.toFixed(2)} J`} />
                    </div>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["enthalpy-change"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Formula</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-mono">q = mcΔT</p>
                <p className="text-sm text-muted-foreground mt-2">
                  where q = heat, m = mass, c = specific heat, ΔT = temperature change
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
